// Hook de integración de la marca de agua en los flujos de descarga.
//
// Contrato: la superficie monta `chooserElement` en su JSX, pregunta UNA vez
// por operación con `resolveWatermarkChoice()` (si no hay config activa
// resuelve 'sin' al toque, sin UI) y pasa cada blob de imagen por
// `applyWatermarkIfChosen` antes de escribirlo/compartirlo. Los videos y los
// errores de composición pasan de largo: mejor sin marca que sin descarga.

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { applyWatermarkToBlob } from '../../utils/watermark';
import {
  fetchWatermarkConfig,
  setLastWatermarkChoice,
  type WatermarkChoice,
} from '../../utils/watermarkConfig';
import { WatermarkDownloadChooser } from './WatermarkDownloadChooser';

export function useWatermarkDownload(): {
  chooserElement: ReactNode;
  /** null = el usuario canceló la descarga desde el chooser. */
  resolveWatermarkChoice: () => Promise<WatermarkChoice | null>;
  applyWatermarkIfChosen: (
    blob: Blob,
    isVideo: boolean,
    choice: WatermarkChoice | null,
  ) => Promise<Blob>;
} {
  const [open, setOpen] = useState(false);
  const resolverRef = useRef<((c: WatermarkChoice | null) => void) | null>(null);
  const { activeBrand } = useBrandProfile();

  const resolveWatermarkChoice = useCallback(async (): Promise<WatermarkChoice | null> => {
    if (!activeBrand?.id) return 'sin';
    const config = await fetchWatermarkConfig(activeBrand.id);
    if (!config?.enabled) return 'sin'; // sin config → descarga directa, como siempre
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpen(true);
    });
  }, [activeBrand?.id]);

  const applyWatermarkIfChosen = useCallback(
    async (blob: Blob, isVideo: boolean, choice: WatermarkChoice | null): Promise<Blob> => {
      if (choice !== 'con' || isVideo || !blob.type.startsWith('image/')) return blob;
      const config = activeBrand?.id ? await fetchWatermarkConfig(activeBrand.id) : null;
      if (!config?.enabled) return blob;
      // Config activa pero sin capas (no debería pasar: guardar vacío desactiva) —
      // re-encodear la imagen para no marcar nada sería puro costo.
      if (!config.logo.visible && !config.text.visible) return blob;
      try {
        return await applyWatermarkToBlob(blob, config, activeBrand?.logo_url ?? null);
      } catch (err) {
        console.warn('watermark: no se pudo aplicar, se descarga sin marca', err);
        return blob;
      }
    },
    [activeBrand?.id, activeBrand?.logo_url],
  );

  const settle = (c: WatermarkChoice | null) => {
    resolverRef.current?.(c);
    resolverRef.current = null;
    setOpen(false);
  };

  const chooserElement = (
    <WatermarkDownloadChooser
      open={open}
      onPick={(c) => {
        setLastWatermarkChoice(c);
        settle(c);
      }}
      onClose={() => settle(null)}
    />
  );

  return { chooserElement, resolveWatermarkChoice, applyWatermarkIfChosen };
}
